import { Episode } from '../../../types';

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface ShotListHeaderProps {
  episode: Episode;
  shotsCount: number;
  polling: boolean;
  analysisCompleted: boolean;
  enableScriptNormalization: boolean;
  rematchingEpisodeAssets: boolean;
  assetReviewRequired: boolean;
  assetReviewAllActive: boolean;
  submittingReview: boolean;
  generatingPrompts: boolean;
  normalizingEpisode: boolean;
  resolvingPronouns: boolean;
  t: Translate;
  onConfirmRematchAssets: () => void;
  onSubmitAssetReview: () => void;
  onGeneratePrompts: () => void;
  onNormalize: () => void;
  onResolvePronouns: () => void;
}

export default function ShotListHeader({
  episode,
  shotsCount,
  polling,
  analysisCompleted,
  enableScriptNormalization,
  rematchingEpisodeAssets,
  assetReviewRequired,
  assetReviewAllActive,
  submittingReview,
  generatingPrompts,
  normalizingEpisode,
  resolvingPronouns,
  t,
  onConfirmRematchAssets,
  onSubmitAssetReview,
  onGeneratePrompts,
  onNormalize,
  onResolvePronouns,
}: ShotListHeaderProps) {
  const needsNormalize = enableScriptNormalization && !episode.normalizedContentURL;
  const needsPronounResolve = !episode.annotatedContentURL && (!enableScriptNormalization || !!episode.normalizedContentURL);
  const canGeneratePrompts = episode.status === 'draft' || episode.status === 'failed' || episode.status === 'completed' || episode.status === 'pending';
  const pendingGenerationQueued = episode.status === 'pending' && episode.promptGenerationStatus === 'generating';

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: shotsCount > 0 ? '1.5rem' : 0 }}>
      <h3 style={{ margin: 0, color: 'var(--theme-storyboard)' }}>{t('shotList.title')} {shotsCount > 0 && t('shotList.count', { count: shotsCount })}</h3>
      {canGeneratePrompts && !pendingGenerationQueued && !polling && (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {needsNormalize ? (
            <button className="btn btn-primary" onClick={onNormalize} disabled={normalizingEpisode}>
              {normalizingEpisode ? <><span className="loading-spinner"></span>{t('shot.submitting')}</> : t('shotList.startNormalize')}
            </button>
          ) : needsPronounResolve ? (
            <button className="btn btn-primary" onClick={onResolvePronouns} disabled={resolvingPronouns}>
              {resolvingPronouns ? <><span className="loading-spinner"></span>{t('shot.submitting')}</> : t('shotList.startPronounResolve')}
            </button>
          ) : !analysisCompleted && (
            <span style={{ fontSize: '0.8rem', color: 'var(--warning)', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span className="loading-spinner" style={{ width: '10px', height: '10px', borderWidth: '2px', borderColor: 'var(--warning)', borderTopColor: 'transparent' }}></span>
              {t('shotList.analysisHint')}
            </span>
          )}
          {!needsNormalize && !needsPronounResolve && (
            <>
              {shotsCount > 0 && (
                <button className="btn btn-secondary" onClick={onConfirmRematchAssets} disabled={rematchingEpisodeAssets || !analysisCompleted}>
                  {rematchingEpisodeAssets ? <><span className="loading-spinner"></span>{t('shotList.rematchingAssets')}</> : t('shotList.rematchAssets')}
                </button>
              )}
              {assetReviewRequired && shotsCount > 0 && (
                <button
                  className="btn btn-secondary"
                  onClick={onSubmitAssetReview}
                  disabled={submittingReview || !analysisCompleted}
                  style={{
                    background: assetReviewAllActive ? 'var(--success)' : undefined,
                    color: assetReviewAllActive ? 'white' : undefined,
                    borderColor: assetReviewAllActive ? 'var(--success)' : undefined,
                  }}
                >
                  {submittingReview ? <><span className="loading-spinner"></span>{t('shotList.submittingReview')}</> :
                   assetReviewAllActive ? t('shotList.reviewPassed') :
                   t('shotList.submitReview')}
                </button>
              )}
              <button className="btn btn-secondary" onClick={onGeneratePrompts} disabled={generatingPrompts}>
                {generatingPrompts ? <><span className="loading-spinner"></span>{t('shotList.generatingPrompts')}</> : shotsCount > 0 ? t('shotList.regeneratePrompts') : t('shotList.generatePrompts')}
              </button>
            </>
          )}
        </div>
      )}
      {pendingGenerationQueued && (
        analysisCompleted ? (
          <span style={{ color: 'var(--warning)', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span className="loading-spinner" style={{ borderColor: 'var(--warning)', borderTopColor: 'transparent' }}></span>{t('shotList.pendingQueue')}
          </span>
        ) : (
          <span style={{ color: 'var(--danger)', fontSize: '0.9rem' }}>
            {t('shotList.pendingNeedAnalysis')}
          </span>
        )
      )}
      {episode.status === 'generating_shots' && (
        <span style={{ color: 'var(--text-2)', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span className="loading-spinner"></span>{t('shotList.generatingShots')}
        </span>
      )}
      {episode.status === 'normalizing' && (
        <span style={{ color: 'var(--text-2)', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span className="loading-spinner"></span>{t('shotList.normalizing')}
        </span>
      )}
      {episode.status === 'pending_normalization' && (
        <span style={{ color: 'var(--text-2)', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span className="loading-spinner"></span>{t('shotList.pendingNormalization')}
        </span>
      )}
      {episode.status === 'pending_pronoun_resolve' && (
        <span style={{ color: 'var(--text-2)', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span className="loading-spinner"></span>{t('shotList.pendingPronounResolve')}
        </span>
      )}
      {episode.status === 'resolving_pronouns' && (
        <span style={{ color: 'var(--text-2)', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span className="loading-spinner"></span>{t('shotList.resolvingPronouns')}
        </span>
      )}
      {episode.status === 'normalizing_failed' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ color: 'var(--danger)', fontSize: '0.9rem' }}>{episode.errorMsg ? t('shotList.normalizeFailedWithMsg', { msg: episode.errorMsg }) : t('shotList.normalizeFailed')}</span>
          <button className="btn btn-primary" onClick={onNormalize} disabled={normalizingEpisode}>
            {normalizingEpisode ? <><span className="loading-spinner"></span>{t('shot.submitting')}</> : t('shotList.reNormalize')}
          </button>
        </div>
      )}
      {episode.status === 'resolving_pronouns_failed' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ color: 'var(--danger)', fontSize: '0.9rem' }}>格式改写失败{episode.errorMsg ? `：${episode.errorMsg}` : ''}</span>
          <button className="btn btn-primary" onClick={onResolvePronouns} disabled={resolvingPronouns}>
            {resolvingPronouns ? <><span className="loading-spinner"></span>{t('shot.submitting')}</> : '重新格式改写'}
          </button>
        </div>
      )}
    </div>
  );
}
